import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconShieldCheck } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { SimpleTable } from "../../../../components/common/simple-table";
import type { SimpleTableColumn } from "../../../../components/common/simple-table";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { ClaimStatusChip } from "../components/claim-status-chip";
import { useBuyerWarrantyClaims } from "../../hooks/use-buyer-warranty-claims";
import type { WarrantyClaimDto } from "../../../../api";

const PAGE_SIZE = 14;

type StatusFilter = "ALL" | "NEW" | "ACKNOWLEDGED" | "INVESTIGATING" | "RESOLVED" | "REJECTED" | "MEDIATING" | "ESCALATED";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "NEW", label: "New" },
  { value: "INVESTIGATING", label: "In Progress" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "REJECTED", label: "Rejected" },
];

const TYPE_LABEL: Record<string, string> = {
  DAMAGE: "Damage",
  DEFECT: "Defect",
  MISSING_PART: "Missing Part",
  DOES_NOT_MATCH_DESCRIPTION: "Mismatch",
  OTHER: "Other",
};

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

const columns: SimpleTableColumn<WarrantyClaimDto>[] = [
  {
    key: "id",
    label: "Claim ID",
    className: "flex-[1.5]",
    render: (r) => (
      <Text variant="bodySm" tone="muted" className="font-mono">
        {r.id.slice(0, 8)}…
      </Text>
    ),
  },
  {
    key: "claimType",
    label: "Type",
    className: "flex-1",
    render: (r) => (
      <Text variant="body" tone="dark">{TYPE_LABEL[r.claimType] ?? r.claimType}</Text>
    ),
  },
  {
    key: "saleOrderId",
    label: "Order",
    className: "flex-1",
    render: (r) => (
      <Text variant="body" tone="body">#{r.saleOrderId}</Text>
    ),
  },
  {
    key: "createdAt",
    label: "Filed",
    className: "flex-[1.2]",
    render: (r) => (
      <Text variant="body" tone="body">{formatDate(r.createdAt)}</Text>
    ),
  },
  {
    key: "status",
    label: "Status",
    className: "flex-[1.2] flex justify-start",
    render: (r) => <ClaimStatusChip status={r.status} />,
  },
];

export function BuyerWarrantyClaimsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const rawStatus = searchParams.get("status") as StatusFilter | null;
  const statusFilter: StatusFilter = rawStatus ?? "ALL";

  const claimsQuery = {
    page,
    pageSize: PAGE_SIZE,
    ...(statusFilter !== "ALL" ? { status: statusFilter } : {}),
  };

  const { data, isLoading } = useBuyerWarrantyClaims(claimsQuery);

  const rows: WarrantyClaimDto[] = data?.data ?? [];
  const pageCount = data?.meta?.pageCount ?? 1;
  const totalCount = data?.meta?.totalCount ?? 0;

  function handleStatusChange(v: string) {
    setPage(1);
    const p = new URLSearchParams(searchParams);
    if (v === "ALL") p.delete("status");
    else p.set("status", v);
    setSearchParams(p, { replace: true });
  }

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
      <Heading variant="h2" tone="dark">My Warranty Claims</Heading>

      <div className="flex gap-4">
        <AccentStatCard bare label="Total claims" value={String(totalCount)} className="flex-1" />
      </div>

      <div className="flex items-center gap-4">
        <SegmentedControl
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={handleStatusChange}
        />
      </div>

      {isLoading ? (
        <ShimmerLoader message="Fetching your warranty claims…" lines={6} />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite">
          <EmptyState
            icon={IconShieldCheck}
            title="No warranty claims"
            description="Claims you've filed for your orders will appear here."
          />
        </div>
      ) : (
        <SimpleTable
          columns={columns}
          data={rows}
          keyExtractor={(r) => r.id}
          onRowClick={(r) =>
            navigate(AppRoutes.MY_WARRANTY_CLAIM_DETAILS.replace(":id", r.id))
          }
          footer={
            pageCount > 1 ? (
              <div className="flex justify-center">
                <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}

export default BuyerWarrantyClaimsPage;
